import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { useNavigate, useLocation } from 'react-router-dom';
import { setUserEmail } from '../services/api';
import { consumePostLoginRedirect, isSafeInternalPath } from '../utils/postLoginRedirect';

interface User {
  id: string;
  name: string;
  email: string;
  photo?: string;
}

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth deve ser usado dentro de um AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const { instance, accounts, inProgress } = useMsal();
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const initializeAuth = async () => {
      // Aguardar até que a interação termine
      if (inProgress !== InteractionStatus.None) {
        return;
      }

      if (accounts.length > 0) {
        const account = accounts[0];

        // Criar user info diretamente da conta
        const userInfo = {
          id: account.localAccountId || '',
          name: account.name || '',
          email: account.username || '',
          photo: account.idTokenClaims?.picture as string,
        };
        setUser(userInfo);

        // Salvar dados de autenticação no localStorage
        localStorage.setItem('authData', JSON.stringify({ user: userInfo }));

        // Definir o email do usuário na API para autenticação
        setUserEmail(userInfo.email);

        // Redirecionar apropriadamente após login.
        // Depois do SSO a página é recarregada e o state do router se perde,
        // então o destino vem do sessionStorage (com state como alternativa).
        const currentPath = window.location.pathname;
        if (currentPath === '/login' || currentPath === '/') {
          const storedRedirect = consumePostLoginRedirect();
          const stateRedirect = (location.state as { redirectTo?: string })?.redirectTo;
          const redirectTo =
            storedRedirect || (isSafeInternalPath(stateRedirect) ? stateRedirect : null);

          if (redirectTo) {
            navigate(redirectTo, { replace: true });
          } else if (currentPath === '/login') {
            navigate('/', { replace: true });
          }
        } else {
          // Destino guardado que não se aplica mais
          consumePostLoginRedirect();
        }
      } else {
        setUser(null);
        setUserEmail(null);
        localStorage.removeItem('authData');
      }
      setIsLoading(false);
    };

    initializeAuth();
  }, [instance, accounts, inProgress, navigate, location]);

  const login = async () => {
    try {
      setIsLoading(true);
      await instance.loginRedirect({
        scopes: ['User.Read', 'email', 'profile', 'openid'],
      });
    } catch (error) {
      console.error('Erro no login:', error);
      setIsLoading(false);
    }
  };

  const logout = async () => {
    try {
      setIsLoading(true);
      await instance.logoutRedirect();
      setUser(null);
      setUserEmail(null);
      localStorage.removeItem('authData');
    } catch (error) {
      console.error('Erro no logout:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const value: AuthContextType = {
    user,
    isLoading,
    login,
    logout,
    isAuthenticated: accounts.length > 0,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
